<template>
  <div class="space-y-5" data-testid="school-picker">
    <div v-if="currentSchool" class="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
      <p class="text-xs font-medium text-emerald-700">Currently working in</p>
      <p class="mt-1 font-semibold text-emerald-950">{{ currentSchool.name }}</p>
    </div>
    <div>
      <label for="school-search" class="mb-2 block text-sm font-medium text-slate-700">Find your school</label>
      <InputText id="school-search" v-model="search" placeholder="Search schools by name" class="w-full"
        autofocus data-testid="school-search" />
    </div>
    <div v-if="error" role="alert" class="rounded-lg bg-red-50 p-4 text-sm text-red-700">
      {{ error }}
      <Button label="Try again" size="small" severity="secondary" class="mt-3" @click="retry" />
    </div>
    <p v-else-if="loading && !schools.length" role="status" class="py-6 text-center text-sm text-slate-500">Loading your schools…</p>
    <p v-else-if="!schools.length" class="py-6 text-center text-sm text-slate-500">
      {{ search ? 'No schools match your search. Try another name.' : "You don't belong to any schools yet." }}
    </p>
    <div v-else class="school-list-container max-h-72 space-y-2 overflow-y-auto" :aria-busy="loading">
      <Button v-for="school in schools" :key="school.id || ''" severity="secondary" outlined
        class="school-card !w-full !justify-start !rounded-xl !p-4 !text-left"
        :disabled="isCurrent(school) || busy"
        :aria-pressed="selected?.id === school.id"
        :class="{ '!border-emerald-600 !bg-emerald-50': selected?.id === school.id }"
        @click="selected = school">
        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100">
          <i class="pi pi-building text-slate-500" aria-hidden="true"></i>
        </span>
        <span class="min-w-0 flex-1">
          <span class="block break-words font-semibold">{{ school.name }}</span>
          <span class="flex items-center gap-2 text-xs text-slate-500">
            <span class="capitalize">{{ school.school_type }}</span>
            <Tag v-if="school.is_parent" value="Parent" icon="pi pi-sitemap" severity="info" rounded
              class="!px-2 !py-0 !text-[10px]" data-testid="school-badge-parent" />
            <Tag v-else-if="school.is_child" value="Campus" icon="pi pi-building" severity="secondary" rounded
              class="!px-2 !py-0 !text-[10px]" data-testid="school-badge-campus" />
          </span>
        </span>
        <span v-if="isCurrent(school)" class="text-xs font-medium">Current</span>
        <i v-else-if="selected?.id === school.id" class="pi pi-check-circle text-emerald-600" aria-label="Selected"></i>
      </Button>
      <Button v-if="hasMore" label="Load more schools" text :loading="loading" class="!w-full" @click="loadMore" />
    </div>
    <div class="border-t border-slate-100 pt-4">
      <p v-if="selected" class="mb-2 text-sm font-medium text-slate-800">Open {{ selected.name }}?</p>
      <p v-if="currentSchool" class="mb-4 text-xs leading-5 text-slate-500">
        You’ll open this school’s dashboard. Save any unfinished work before switching. Your other open tabs will switch too.
      </p>
      <p v-if="switchError" role="alert" class="mb-3 text-sm text-red-600">{{ switchError }}</p>
      <div class="flex justify-end gap-2">
        <Button v-if="currentSchool" label="Cancel" severity="secondary" text :disabled="busy" @click="$emit('cancel')" />
        <Button :label="currentSchool ? 'Switch school' : 'Open school'" icon="pi pi-arrow-right" iconPos="right"
          :disabled="!selected || busy" :loading="busy" data-testid="confirm-school-switch" @click="confirmSwitch" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import { useUserStore } from '@/store/userStore';
import { useSchoolPickerQuery } from '@/composables/queries/useSchoolPickerQuery';
import { reloadForSchool } from '@/services/schoolSwitch';
import type { School } from '@/types/composition-functions';

const props = defineProps<{ returnTo?: string | null }>();
defineEmits<{ cancel: [] }>();
const userStore = useUserStore();
const currentSchool = computed(() => userStore.currentSchool);
const { search, schools, loading, error, hasMore, loadMore, retry } = useSchoolPickerQuery();
const selected = ref<School | null>(null);
const busy = ref(false);
const switchError = ref('');
const isCurrent = (school: School) => !!currentSchool.value && String(school.id) === String(currentSchool.value.id);

async function confirmSwitch() {
  if (!selected.value || busy.value || isCurrent(selected.value)) return;
  busy.value = true;
  switchError.value = '';
  try {
    await reloadForSchool(selected.value, false, props.returnTo);
  } catch {
    switchError.value = 'We could not switch schools. Please try again.';
    busy.value = false;
  }
}
</script>
